import { create } from "zustand";
import { BatchRawFileItemState } from "@presentation/common";
import { BatchRawFile, FileMetadata } from "@domain/schemas/BI";
import { State } from "@progress/kendo-data-query";
import { loadRuntimeConfig, runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";

await loadRuntimeConfig(); //seems like the one loaded in index.tsx is not available here, so we load it again

export type SelectedState = { 
  [id: string]: FileMetadata & { isFolder: boolean }
};

interface FileExplorerStore {
  rawFiles: BatchRawFileItemState[];
  metaState: State;
  selectedState: SelectedState;
  breadcrumbItems: { id: string; text: string; disabled: boolean }[];
  resetSelectedGridData: () => void;
  setRawFiles: (newRawFileList: BatchRawFile[]) => void;
  setSelectedState: (newSelectedState: SelectedState) => void;
  setBreadcrumbItems: (items: { id: string; text: string; disabled: boolean }[]) => void;
  setMetaState: (state: State) => void;
}

export const useFileExplorerStore = create<FileExplorerStore>((set) => ({
  rawFiles: [],
  metaState: {
    skip: 0,          // start at first page
    take: 50,         // default page size
    sort: [{ field: "fileName", dir: "asc" }],
    filter: undefined // optional, ensures no filter applied initially
  },
  selectedState: {},
  breadcrumbItems: [{ id: "home", text: "Location: " + runtimeConfig.RAWFILE_CONTAINER_NAME, disabled: false }],
  setBreadcrumbItems: (items) => {
    set({ breadcrumbItems: items });
  },
  setRawFiles: (newRawFileList: BatchRawFile[]) => {
    set((state) => {
      const existingRawFileMap = new Map(state.rawFiles.map((rawFile) => [rawFile.id, rawFile]));
      const updatedRawFileList = newRawFileList.map((rawFile) => {
        const existingRawFile = existingRawFileMap.get(rawFile.id);

        return {
          ...rawFile,
          isSelected: existingRawFile ? existingRawFile.isSelected : false,
          isExpanded: existingRawFile ? existingRawFile.isExpanded : false,
          sizeInBytes: rawFile.sizeInBytes || 0,
        } as BatchRawFileItemState;
      });

      return { rawFiles: updatedRawFileList };
    });
  },
  setSelectedState: (newSelectedState: SelectedState) => set({ selectedState: newSelectedState }),
  resetSelectedGridData: () => {
    set({ selectedState: {} });
  },
  setMetaState: (state: State) => {
    set({ metaState: state });
  },
}));
